<template>
  <AppLink
    v-if="onlyOneChild.meta"
    :to="resolvePath(onlyOneChild.path)"
    class="single-sidebar-item"
    :class="{ 'is-collapsed': isCollapsed }"
  >
    <el-menu-item
      :index="resolvePath(onlyOneChild.path)"
      :class="
        `menu-item ${
          isNest ? 'menu-item--without-dropdown' : 'menu-item--with-dropdown'
        }`
      "
    >
      <svg-icon
        icon-class="active-menu-icon"
        class="active-menu-icon"
        color="#8267DB"
        width="6px"
        height="30px"
      />
      <div class="menu-item__inner">
        <SidebarItemTooltip :item="onlyOneChild" :is-collapsed="isCollapsed" />
        <div
          v-if="onlyOneChild.meta.label && (!isCollapsed || isNest)"
          class="menu-item__label"
        >
          {{ $t(onlyOneChild.meta.label) }}
        </div>
        <div
          v-if="shouldDisplayTag"
          class="single-sidebar-item__notification"
          :class="tagTypeClass"
        >
          {{ $t(onlyOneChild.meta.tag.label) }}
        </div>
        <svg-icon
          v-if="onlyOneChild.meta.isExternal"
          color="#ccc"
          class="menu-item__suffix--external-icon"
          icon-class="arrow-out"
        />
        <div
          v-if="hightLightLabel"
          class="single-sidebar-item__hightlight__count"
        >
          {{ hightLightLabel }}
        </div>
      </div>
    </el-menu-item>
  </AppLink>
</template>

<script>
import { mapState } from 'vuex'
import AppLink from '@/components/SidebarUtils/Link.vue'
import ResolvePathMixin from '@/mixins/resolve-path'
import SidebarItemTooltip from '@/components/SidebarUtils/Tooltip.vue'
import { isFunction } from '@/utils/validate'

export default {
  name: 'SingleSidebarItem',
  components: { SidebarItemTooltip, AppLink },
  mixins: [ResolvePathMixin],
  props: {
    basePath: {
      type: String,
      default: '',
    },
    onlyOneChild: {
      type: Object,
      required: true,
    },
    isNest: {
      type: Boolean,
      default: false,
    },
    isKycRoute: {
      type: Boolean,
      default: false,
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('order', ['authorisedOrdersRecords']),
    ...mapState('dispute', [
      'actionRequiredCountSideBar',
      'staticOpenCasesCount',
    ]),
    shouldDisplayHighlightCount() {
      return ['/orders', '/dispute'].includes(this.basePath)
    },
    orderCount() {
      return this.shouldDisplayHighlightCount &&
        this.authorisedOrdersRecords > 99
        ? '99+'
        : this.authorisedOrdersRecords || null
    },
    disputeCount() {
      return this.shouldDisplayHighlightCount &&
        this.actionRequiredCountSideBar > 99
        ? '99+'
        : this.actionRequiredCountSideBar || null
    },
    hightLightLabel() {
      return this.basePath === '/orders'
        ? this.orderCount
        : this.basePath === '/dispute'
        ? this.disputeCount
        : ''
    },
    tagTypeClass() {
      if (!this.shouldDisplayTag) {
        return ''
      }

      return `is-${this.onlyOneChild.meta.tag.type}`
    },
    shouldDisplayTag() {
      if (!this.onlyOneChild?.meta?.tag) {
        return false
      }

      const tag = this.onlyOneChild?.meta?.tag

      if (isFunction(tag.visible)) {
        return tag.visible(this.$store)
      }

      return true
    },
  },
  methods: {
    resolvePath(routePath) {
      return this.resolvePathWithBasePath(this.basePath, routePath)
    },
  },
}
</script>

<style lang="scss">
.single-sidebar-item {
  --single-sidebar-item-width: 100%;
  --single-sidebar-item-height: 100%;

  width: var(--single-sidebar-item-width);
  height: var(--single-sidebar-item-height);

  &__notification {
    --sidebar-item-tag-bg-color: transparent;

    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    color: var(--warning-text-color);
    text-align: start;
    background-color: var(--sidebar-item-tag-bg-color);
    border-radius: 50%;
    &.is-warning {
      --sidebar-item-tag-bg-color: #8267db;
    }
  }

  &__hightlight__count {
    position: relative;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    margin: 0 24px 0 auto;
    [dir='rtl'] & {
      margin: 0 auto 0 24px;
    }

    font-size: 12px;
    font-weight: 500;
    color: var(--sidebar-item-hight-light-count-color);
    text-align: start;
    background-color: var(--sidebar-item-hight-light-count-bg);
    border-radius: 60px;
  }

  .menu-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding-inline: 0 !important;
    color: inherit !important;
    background-color: transparent !important;

    &__inner {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      justify-content: flex-start;
      height: var(--single-sidebar-item-height);
      padding: 10px 13px;
      margin-inline-end: 13px;
      transition: background-color 0.3s ease-in-out;

      &:hover {
        background-color: #f0e8ff !important;
        border-radius: 5px;
        transition: all 0.3s ease-in;
      }
    }

    &__label {
      flex: 1 1 auto;
      height: 100%;
      max-height: 100%;
      margin-inline-start: 8px;
      font-family: var(--font-family--inter), sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
      color: var(--neutral-black-text-500);
    }

    &__suffix--external-icon {
      font-size: 24px;

      [dir='rtl'] & {
        transform: rotate(270deg);
      }
    }

    .active-menu-icon {
      margin-inline-end: 7px;
    }

    &.is-active {
      .menu-item__inner {
        background-color: #f0e8ff !important;
        border-radius: 5px;
        transition: all 0.3s ease-in;
      }
    }
  }

  &.is-collapsed {
    .menu-item {
      .active-menu-icon {
        display: none;
      }

      .menu-item__inner {
        margin-inline: 21px;
      }
    }
  }
}
</style>
